import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon, EnterFullScreenIcon, Cross2Icon } from '@radix-ui/react-icons';
import { motion } from 'framer-motion';
import { useTheme } from '../../../Theme/ThemeProvider';
import { useDispatch } from 'react-redux';
import { openImage } from '../../../../store/chatSlice';
import ExcelJS from 'exceljs';
import { convertFileToPdf } from '../../../../utils/document/fileConversion.service';
import { useMsal } from '@azure/msal-react';
import logger from "../../../../utils/logger/logger";
import { utf8ToBase64 } from '../../../../utils/base64.utils';

const FULLSCREEN_ANIM_MS = 380;

function buildPdfViewerSrc(src: string, zoom: number = 50) {
  void zoom;
  return `${src}#toolbar=1&navpanes=1&pagemode=thumbs`;
}

function pdfViewerNameOverlay(fileName: string, pdfSrc?: string) {
  void pdfSrc;
  const overlayWidth = 'clamp(170px, 17vw, 235px)';
  const overlayCoverWidth = 'clamp(185px, 18vw, 255px)';
  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: overlayCoverWidth,
          height: '64px',
          zIndex: 3,
          background: 'linear-gradient(90deg, rgb(60, 60, 60) 0%, rgb(60, 60, 60) 90%, rgba(60, 60, 60, 0.94) 96%, rgba(60, 60, 60, 0) 100%)',
          pointerEvents: 'none'
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '8px',
          left: '0',
          width: overlayWidth,
          height: '48px',
          zIndex: 4,
          color: '#fff',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px 0 16px',
          boxSizing: 'border-box'
        }}
        title={fileName}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '3px',
            width: '18px',
            height: '18px',
            marginRight: '12px',
            opacity: 0.95,
            flexShrink: 0
          }}
        >
          {[0, 1, 2].map((line) => (
            <span
              key={line}
              style={{
                display: 'block',
                width: '12px',
                height: '2px',
                borderRadius: '999px',
                background: 'rgba(255, 255, 255, 0.92)'
              }}
            />
          ))}
        </span>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            maxWidth: 'calc(100% - 36px)',
            lineHeight: 1
          }}
        >
          <span
            style={{
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#fff',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: '1 1 auto',
              minWidth: 0
            }}
          >
            {fileName}
          </span>
        </span>
      </div>
    </>
  );
}

// ── Fullscreen table / text modal ──────────────────────────────────
export function FullscreenDataModal({
  isOpen,
  onClose,
  title,
  children,
  size = 'default',
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'default' | 'viewer';
}) {
  const [shouldRender, setShouldRender] = React.useState(isOpen);
  const [isVisible, setIsVisible] = React.useState(isOpen);
  // Preserve content during exit animation so the fade-out isn't blank
  const lastChildrenRef = React.useRef<React.ReactNode>(children);
  const lastTitleRef = React.useRef(title);

  if (children) {
    lastChildrenRef.current = children;
    lastTitleRef.current = title;
  }

  React.useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const id = window.requestAnimationFrame(() => setIsVisible(true));
      return () => window.cancelAnimationFrame(id);
    }
    if (!shouldRender) return undefined;
    setIsVisible(false);
    const t = window.setTimeout(() => setShouldRender(false), FULLSCREEN_ANIM_MS);
    return () => window.clearTimeout(t);
  }, [isOpen, shouldRender]);

  React.useEffect(() => {
    if (!shouldRender) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose, shouldRender]);

  if (!shouldRender) return null;

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.78)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      initial={false}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ duration: isVisible ? 0.22 : 0.32, ease: 'easeOut' }}
    >
      {/* Close button outside the card, matching ImageModal style */}
      <motion.button
        type="button"
        onClick={onClose}
        aria-label="Close fullscreen preview"
        className="absolute right-4 top-4 z-10 rounded-md"
        style={{
          background: 'rgba(0, 0, 0, 0.6)',
          color: 'white',
          padding: '8px',
          borderRadius: '6px',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px'
        }}
        initial={false}
        animate={{ opacity: isVisible ? 0.7 : 0, y: isVisible ? 0 : -8 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        <Cross2Icon width={20} height={20} />
      </motion.button>

      <motion.div
        className="relative w-full max-w-6xl border border-white/10 bg-[var(--color-secondary)] shadow-2xl flex flex-col"
        style={{
          width: size === 'viewer' ? '90vw' : undefined,
          maxWidth: size === 'viewer' ? '90vw' : undefined,
          height: size === 'viewer' ? '90vh' : undefined,
          maxHeight: size === 'viewer' ? '90vh' : '92vh',
          color: 'var(--color-text)',
          borderRadius: '0px',
        }}
        onClick={(e) => e.stopPropagation()}
        initial={false}
        animate={{
          opacity: isVisible ? 1 : 0,
          scale: isVisible ? 1 : 0.96,
          y: isVisible ? 0 : 18,
        }}
        transition={{ duration: isVisible ? 0.26 : 0.32, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="border-b border-[var(--color-accent)]/20 px-6 py-3">
          <div className="truncate text-sm font-semibold" title={lastTitleRef.current}>{lastTitleRef.current}</div>
        </div>
        <div
          className="flex-1 overflow-auto px-6 py-4"
          style={{ maxHeight: size === 'viewer' ? 'calc(90vh - 56px)' : 'calc(92vh - 56px)' }}
        >
          {lastChildrenRef.current}
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}


export interface FullscreenTableData { rows: string[][]; title: string; }
export interface FullscreenTextData { text: string; title: string; }

interface FilePreviewProps {
  fileData: string | ArrayBuffer;
  fileName: string;
  fileType?: string;
  onError?: () => void;
  onLoad?: () => void; // Called when image/PDF finishes loading
  onOpenFullscreen?: () => void;
  onRequestFullscreenTable?: (data: FullscreenTableData) => void;
  onRequestFullscreenText?: (data: FullscreenTextData) => void;
  /** When true, show all data without truncation (no row/line limits, no expand button) */
  fullscreenMode?: boolean;
  /** Called with a data-URI PDF string when PPTX/DOCX conversion succeeds. When set, the component skips rendering its own fullscreen PDF viewer and lets the caller handle display. */
  onConvertedPdfReady?: (pdfUrl: string) => void;
}

const FilePreviewComponent: React.FC<FilePreviewProps> = ({ 
  fileData, 
  fileName, 
  fileType,
  onError,
  onLoad,
  onOpenFullscreen,
  onRequestFullscreenTable,
  onRequestFullscreenText,
  fullscreenMode,
  onConvertedPdfReady,
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const dispatch = useDispatch();
  const [previewError, setPreviewError] = useState(false);
  const [fullscreenTable, setFullscreenTable] = useState<{ rows: string[][]; title: string } | null>(null);
  const [fullscreenText, setFullscreenText] = useState<{ text: string; title: string } | null>(null);
  const { instance: msalInstance } = useMsal();
  
  // Use refs to store URLs so they persist across re-renders
  const imageUrlRef = useRef<string | null>(null);
  const pdfUrlRef = useRef<string | null>(null);
  // Cache stable PDF data URLs for converted PPT/Word PDFs
  const pptPdfUrlRef = useRef<string | null>(null);
  const docPdfUrlRef = useRef<string | null>(null);
  
  // State for PowerPoint conversion
  const [pptConversionState, setPptConversionState] = useState<{
    status: 'idle' | 'converting' | 'converted' | 'error';
    pdfData?: string;
    error?: string;
  }>({ status: 'idle' });
  
  // State for Word document conversion
  const [docConversionState, setDocConversionState] = useState<{
    status: 'idle' | 'converting' | 'converted' | 'error';
    pdfData?: string;
    error?: string;
  }>({ status: 'idle' });
  const [showPptInfoBanner, setShowPptInfoBanner] = useState(true);
  const [showDocInfoBanner, setShowDocInfoBanner] = useState(true);
  
  // State for Excel parsing
  const [excelData, setExcelData] = useState<{
    status: 'idle' | 'parsing' | 'parsed' | 'error';
    csvData?: string;
    error?: string;
  }>({ status: 'idle' });
  
  const initializedRef = useRef<boolean>(false); // Track if URLs have been created

  useEffect(() => {
    setShowPptInfoBanner(true);
    setShowDocInfoBanner(true);
  }, [fileName]);

  const renderConvertedPdfPreview = React.useCallback(({
    pdfUrl,
    bannerTitle,
    bannerMessage,
    showInfoBanner,
    onDismissInfoBanner,
    loadLogMessage,
    errorLogMessage,
  }: {
    pdfUrl: string;
    bannerTitle: string;
    bannerMessage: string;
    showInfoBanner: boolean;
    onDismissInfoBanner: () => void;
    loadLogMessage: string;
    errorLogMessage: string;
  }) => (
    <div style={{
      marginBottom: '12px',
      display: fullscreenMode ? 'flex' : 'block',
      flexDirection: fullscreenMode ? 'column' : undefined,
      height: fullscreenMode ? 'calc(92vh - 120px)' : undefined,
      minHeight: fullscreenMode ? 0 : undefined,
      border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
      borderRadius: '0px',
      overflow: 'hidden',
      background: isDark ? '#1f1f1f' : '#fff'
    }}>
      {showInfoBanner && (
        <div style={{
          padding: '10px 12px',
          background: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
          borderBottom: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
          fontSize: '0.85em',
          color: isDark ? '#93c5fd' : '#2563eb',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px'
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 500, marginBottom: '4px' }}>
              {bannerTitle}
            </div>
            <div style={{ opacity: 0.9, lineHeight: '1.4' }}>
              {bannerMessage}
            </div>
          </div>
          <button
            type="button"
            onClick={onDismissInfoBanner}
            aria-label="Dismiss preview info"
            style={{
              border: 'none',
              background: 'transparent',
              color: 'inherit',
              cursor: 'pointer',
              padding: '2px',
              lineHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              opacity: 0.85
            }}
          >
            <Cross2Icon width={16} height={16} />
          </button>
        </div>
      )}

      <div
        style={{
          overflow: 'hidden',
          position: 'relative',
          flex: fullscreenMode ? 1 : undefined,
          minHeight: fullscreenMode ? 0 : undefined,
        }}
      >
        {pdfViewerNameOverlay(fileName, pdfUrl)}
        <iframe
          src={buildPdfViewerSrc(pdfUrl, 50)}
          onLoad={() => {
            logger.log(loadLogMessage);
            if (onLoad) onLoad();
          }}
          onError={() => {
            console.error(errorLogMessage);
            if (onError) onError();
          }}
          style={{
            width: '100%',
            height: fullscreenMode ? '100%' : '400px',
            border: 'none',
            display: 'block'
          }}
          title={`PDF preview of ${fileName}`}
        />
        {!fullscreenMode && (
          <button
            type="button"
            onClick={() => {
              onOpenFullscreen?.();
              dispatch(openImage({ src: pdfUrl, type: 'pdf', name: fileName, useCustomPdfHeader: true }));
            }}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              border: 'none',
              background: 'rgba(0, 0, 0, 0.7)',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.85)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)'}
            title="Open in fullscreen"
            aria-label="Open converted PDF in fullscreen"
          >
            <EnterFullScreenIcon width={16} height={16} />
          </button>
        )}
      </div>
    </div>
  ), [dispatch, fileName, fullscreenMode, isDark, onError, onLoad, onOpenFullscreen]);

  // Determine file type from filename extension and content-type
  // Special case: Backend may convert Excel to CSV text for preview
  const detectedType = useMemo(() => {
    logger.log('[FilePreview] Detecting type:', { fileName, fileType, fileDataType: typeof fileData });
    
    const ext = fileName.split('.').pop()?.toLowerCase();
    logger.log('[FilePreview] File extension:', ext);

    // Prefer explicit MIME type when available. This is important for files that
    // were converted before preview (for example `.txt` / `.docx` -> PDF) but still
    // retain the original filename for display.
    if (fileType) {
      if (fileType === 'application/pdf') {
        return 'pdf';
      }
      if (fileType.startsWith('image/')) {
        return 'image';
      }
      if (
        fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        fileType === 'application/vnd.ms-excel'
      ) {
        return 'excel';
      }
      if (
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        fileType === 'application/msword'
      ) {
        return 'word';
      }
      if (
        fileType === 'text/csv' ||
        fileType === 'application/csv'
      ) {
        return 'csv';
      }
      if (
        fileType === 'text/tab-separated-values'
      ) {
        return 'tsv';
      }
      if (
        fileType.startsWith('text/') ||
        fileType === 'application/json'
      ) {
        return fileType === 'application/json' ? 'json' : 'text';
      }
    }
    
    // Check file extension first for most cases
    switch (ext) {
      // Images: PNG, JPG, SVG, PDF (plots)
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'webp':
      case 'svg':
        return 'image';
      
      // Spreadsheets and data (all displayed as tables)
      case 'csv':
        return 'csv';
      case 'tsv':
        return 'tsv';
      case 'xlsx':
      case 'xls':
        return 'excel';
      case 'json':
        return 'json';
      
      // Documents
      case 'pdf':
        return 'pdf';
      case 'ppt':
      case 'pptx':
        return 'powerpoint';
      case 'doc':
      case 'docx':
        return 'word';
      
      // Text and markup (all displayed as text)
      case 'txt':
      case 'log':
      case 'md':
      case 'markdown':
      case 'html':
      case 'htm':
        return 'text';
      
      // Archives (no preview)
      case 'zip':
        return 'archive';
    }
    
    // Default to unknown
    return 'unknown';
  }, [fileName, fileType, fileData]);

  // Convert PowerPoint to PDF for preview
  useEffect(() => {
    if (detectedType !== 'powerpoint' || pptConversionState.status !== 'idle') {
      return;
    }

    const convertPowerPoint = async () => {
      logger.log('[FilePreview] Starting PowerPoint to PDF conversion:', fileName);
      setPptConversionState({ status: 'converting' });

      try {
        // Convert ArrayBuffer to File object
        let fileBlob: Blob;
        if (typeof fileData === 'string') {
          // Base64 string - decode it
          const binaryString = atob(fileData);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          fileBlob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
        } else {
          fileBlob = new Blob([fileData], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
        }

        const file = new File([fileBlob], fileName, {
          type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        });

        // Get conversion endpoint from env
        const apimBaseUrl = import.meta.env.VITE_APIM_BASE_URL || 'https://navigator.ai.3m.com';
        const conversionEndpoint = import.meta.env.VITE_FILE_CONVERSION_URL 
          || `${apimBaseUrl}/nav-api/api/ai/convert-to-pdf`;

        // Convert using the existing service
        const result = await convertFileToPdf(file, conversionEndpoint, msalInstance);

        // CRITICAL: convertFileToPdf never throws — on failure it silently returns
        // the original file data with original MIME type. We must validate.
        if (result.type !== 'application/pdf') {
          throw new Error('Conversion service returned original file instead of PDF — server conversion likely failed');
        }

        logger.log('[FilePreview] PowerPoint conversion successful:', result.name);
        setPptConversionState({
          status: 'converted',
          pdfData: result.data
        });
      } catch (error) {
        console.error('[FilePreview] PowerPoint conversion failed:', error);
        setPptConversionState({
          status: 'error',
          error: error instanceof Error ? error.message : 'Conversion failed'
        });
      }
    };

    convertPowerPoint();
  }, [detectedType, fileName, fileData, msalInstance, pptConversionState.status]);

  // Convert Word document to PDF for preview
  useEffect(() => {
    if (detectedType !== 'word' || docConversionState.status !== 'idle') {
      return;
    }

    const convertWord = async () => {
      logger.log('[FilePreview] Starting Word to PDF conversion:', fileName);
      setDocConversionState({ status: 'converting' });

      try {
        const wordMime = fileName.toLowerCase().endsWith('.doc')
          ? 'application/msword'
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

        let fileBlob: Blob;
        if (typeof fileData === 'string') {
          const binaryString = atob(fileData);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          fileBlob = new Blob([bytes], { type: wordMime });
        } else {
          fileBlob = new Blob([fileData], { type: wordMime });
        }

        const file = new File([fileBlob], fileName, { type: wordMime });

        const apimBaseUrl = import.meta.env.VITE_APIM_BASE_URL || 'https://navigator.ai.3m.com';
        const conversionEndpoint = import.meta.env.VITE_FILE_CONVERSION_URL
          || `${apimBaseUrl}/nav-api/api/ai/convert-to-pdf`;

        const result = await convertFileToPdf(file, conversionEndpoint, msalInstance);

        // CRITICAL: convertFileToPdf never throws — on failure it silently returns
        // the original file data with original MIME type. We must validate.
        if (result.type !== 'application/pdf') {
          throw new Error('Conversion service returned original file instead of PDF — server conversion likely failed');
        }

        logger.log('[FilePreview] Word conversion successful:', result.name);
        setDocConversionState({
          status: 'converted',
          pdfData: result.data,
        });
      } catch (error) {
        console.error('[FilePreview] Word conversion failed:', error);
        setDocConversionState({
          status: 'error',
          error: error instanceof Error ? error.message : 'Conversion failed',
        });
      }
    };

    convertWord();
  }, [detectedType, fileName, fileData, msalInstance, docConversionState.status]);

  // Notify caller when PowerPoint conversion completes (so they can open ImageModal directly)
  useEffect(() => {
    if (pptConversionState.status === 'converted' && pptConversionState.pdfData && onConvertedPdfReady) {
      onConvertedPdfReady(`data:application/pdf;base64,${pptConversionState.pdfData}`);
    }
  }, [pptConversionState.status, pptConversionState.pdfData, onConvertedPdfReady]);

  // Notify caller when Word conversion completes (so they can open ImageModal directly)
  useEffect(() => {
    if (docConversionState.status === 'converted' && docConversionState.pdfData && onConvertedPdfReady) {
      onConvertedPdfReady(`data:application/pdf;base64,${docConversionState.pdfData}`);
    }
  }, [docConversionState.status, docConversionState.pdfData, onConvertedPdfReady]);

  // Parse Excel files
  useEffect(() => {
    if (detectedType !== 'excel' || excelData.status !== 'idle') {
      return;
    }

    const parseExcel = async (data: ArrayBuffer): Promise<string[][]> => {
      try {
        logger.log('[FilePreview] Parsing Excel file:', { fileName, byteLength: data.byteLength });
        
        // Read the workbook with ExcelJS
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(data);
        
        // Get the first worksheet
        const worksheet = workbook.worksheets[0];
        
        logger.log('[FilePreview] Excel parsed:', {
          fileName,
          sheetName: worksheet.name,
          sheetCount: workbook.worksheets.length
        });
        
        // Convert to array of arrays
        const rows: string[][] = [];
        worksheet.eachRow((row: any, rowNumber: number) => {
          const rowValues: string[] = [];
          row.eachCell({ includeEmpty: true }, (cell: any, colNumber: number) => {
            // Convert cell value to string
            const value = cell.value;
            if (value === null || value === undefined) {
              rowValues.push('');
            } else if (typeof value === 'object' && 'text' in value) {
              // Rich text
              rowValues.push(value.text || '');
            } else if (typeof value === 'object' && 'result' in value) {
              // Formula
              rowValues.push(String(value.result || ''));
            } else {
              rowValues.push(String(value));
            }
          });
          rows.push(rowValues);
        });
        
        return rows;
      } catch (error) {
        console.error('[FilePreview] Excel parsing error:', error);
        throw error;
      }
    };

    const parseExcelFile = async () => {
      logger.log('[FilePreview] Starting Excel parsing:', fileName);
      setExcelData({ status: 'parsing' });

      try {
        let buffer: ArrayBuffer;
        if (typeof fileData === 'string') {
          // Convert string to ArrayBuffer
          const bytes = new Uint8Array(fileData.length);
          for (let i = 0; i < fileData.length; i++) {
            bytes[i] = fileData.charCodeAt(i);
          }
          buffer = bytes.buffer;
        } else {
          buffer = fileData;
        }

        const rows = await parseExcel(buffer);
        // Properly escape cells containing commas, quotes, or newlines for CSV format
        const csvData = rows.map(row => 
          row.map(cell => {
            // If cell contains comma, quote, or newline, wrap in quotes and escape internal quotes
            if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
              return `"${cell.replace(/"/g, '""')}"`;
            }
            return cell;
          }).join(',')
        ).join('\n');
        
        logger.log('[FilePreview] Excel parsing successful:', fileName);
        setExcelData({
          status: 'parsed',
          csvData
        });
      } catch (error) {
        console.error('[FilePreview] Excel parsing failed:', error);
        setExcelData({
          status: 'error',
          error: error instanceof Error ? error.message : 'Parsing failed'
        });
      }
    };

    parseExcelFile();
  }, [detectedType, fileName, fileData, excelData.status]);
  
  // Call onLoad after Excel data is parsed and ready to render
  useEffect(() => {
    if (detectedType === 'excel' && excelData.status === 'parsed' && onLoad) {
      // Use requestAnimationFrame to ensure the table has actually rendered
      requestAnimationFrame(() => {
        onLoad();
      });
    }
  }, [detectedType, excelData.status, onLoad]);

  // Create stable blob URLs synchronously during render using useMemo
  // CRITICAL: NO dependencies - create once and never recreate
  // The ref persists across all re-renders, preventing URL recreation on scroll
  const imageUrl = useMemo(() => {
    if (detectedType !== 'image') {
      return null;
    }
    
    // If we already have a URL in the ref, ALWAYS reuse it (prevents re-creation on every render)
    if (imageUrlRef.current) {
      return imageUrlRef.current;
    }
    
    // Only log once when actually creating
    if (!initializedRef.current) {
      logger.log('[FilePreview] Creating image URL:', { fileName, fileDataType: typeof fileData, fileDataLength: typeof fileData === 'string' ? fileData.length : (fileData as ArrayBuffer).byteLength });
    }
    
    let url: string;
    if (typeof fileData === 'string') {
      // Check if it's SVG content (starts with XML or SVG tag)
      const isSvgContent = fileData.trim().startsWith('<svg') || fileData.trim().startsWith('<?xml');
      
      if (isSvgContent) {
        // For SVG, create proper data URL with correct MIME type
        // Use UTF-8 safe encoding in case SVG contains special characters
        url = `data:image/svg+xml;base64,${utf8ToBase64(fileData)}`;
      } else if (fileData.startsWith('data:')) {
        url = fileData;
      } else {
        // Detect image type from file extension for proper MIME type
        const ext = fileName.split('.').pop()?.toLowerCase();
        const mimeType = ext === 'svg' ? 'image/svg+xml' : 
                        ext === 'png' ? 'image/png' :
                        ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' :
                        ext === 'gif' ? 'image/gif' :
                        ext === 'webp' ? 'image/webp' : 'image/png';
        url = `data:${mimeType};base64,${fileData}`;
      }
    } else {
      // Detect MIME type from file extension
      const ext = fileName.split('.').pop()?.toLowerCase();
      const mimeType = ext === 'svg' ? 'image/svg+xml' : 
                      ext === 'png' ? 'image/png' :
                      ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' :
                      ext === 'gif' ? 'image/gif' :
                      ext === 'webp' ? 'image/webp' : 
                      fileType || 'image/png';
      
      const blob = new Blob([fileData], { type: mimeType });
      url = URL.createObjectURL(blob);
      if (!initializedRef.current) {
        logger.log('[FilePreview] Created blob URL for image:', url);
      }
    }
    
    imageUrlRef.current = url;
    initializedRef.current = true;
    return url;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // NO dependencies - truly stable across all re-renders

  const pdfUrl = useMemo(() => {
    if (detectedType !== 'pdf') {
      return null;
    }
    
    // If we already have a URL in the ref, ALWAYS reuse it (prevents re-creation on every render)
    if (pdfUrlRef.current) {
      return pdfUrlRef.current;
    }
    
    // Only log once when actually creating
    if (!initializedRef.current) {
      logger.log('[FilePreview] Creating PDF URL:', { 
        fileName, 
        fileDataType: typeof fileData, 
        fileDataLength: typeof fileData === 'string' ? fileData.length : (fileData as ArrayBuffer).byteLength,
        firstBytes: typeof fileData === 'string' 
          ? fileData.substring(0, 50) 
          : new TextDecoder().decode((fileData as ArrayBuffer).slice(0, 50))
      });
    }
    
    let url: string;
    if (typeof fileData === 'string') {
      // Check if it's already a blob URL
      if (fileData.startsWith('blob:')) {
        url = fileData;
        if (!initializedRef.current) {
          logger.log('[FilePreview] Using existing URL for PDF:', url.substring(0, 50));
        }
      } else if (fileData.startsWith('data:application/pdf')) {
        url = fileData;
        if (!initializedRef.current) {
          logger.log('[FilePreview] Using data URI for PDF:', { fileName, length: url.length });
        }
      } else {
        // Use a blob URL for native PDF payloads to avoid very large data URI issues.
        let bytes: Uint8Array;
        try {
          const sanitized = fileData.replace(/\s+/g, '');
          const binaryString = atob(sanitized);
          bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
        } catch {
          bytes = new Uint8Array(fileData.length);
          for (let i = 0; i < fileData.length; i++) {
            bytes[i] = fileData.charCodeAt(i);
          }
        }
        const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
        url = URL.createObjectURL(blob);
        if (!initializedRef.current) {
          logger.log('[FilePreview] Created blob URL for PDF from string payload:', { fileName, bytesLength: bytes.length, url: url.substring(0, 50) });
        }
      }
    } else {
      const blob = new Blob([fileData], { type: 'application/pdf' });
      url = URL.createObjectURL(blob);
      if (!initializedRef.current) {
        logger.log('[FilePreview] Created blob URL for PDF from ArrayBuffer:', { fileName, arrayBufferLength: (fileData as ArrayBuffer).byteLength, url: url.substring(0, 50) });
      }
    }
    
    pdfUrlRef.current = url;
    initializedRef.current = true;
    return url;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // NO dependencies - truly stable across all re-renders

  // NOTE: We intentionally do NOT cleanup blob URLs because:
  // 1. React 18 Strict Mode causes unmount/remount in dev, triggering cleanup before browser loads images
  // 2. Blob URLs are lightweight and will be garbage collected when the page unloads
  // 3. Attempting cleanup causes ERR_FILE_NOT_FOUND errors


  const parseCSV = (csvText: string, delimiter: string = ','): string[][] => {
    const lines = csvText.split(/\r?\n/).filter(line => line.trim());
    return lines.map(line => {
      // CSV parser that handles quoted fields with embedded delimiters
      const cells: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];
        
        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            // Escaped quote (two quotes in a row)
            current += '"';
            i++; // Skip the next quote
          } else {
            // Toggle quote state
            inQuotes = !inQuotes;
          }
        } else if (char === delimiter && !inQuotes) {
          // End of cell
          cells.push(current);
          current = '';
        } else {
          current += char;
        }
      }
      cells.push(current);
      return cells;
    });
  };

  const parseJSON = (jsonText: string): string => {
    try {
      logger.log('[FilePreview] Parsing JSON file:', { fileName, textLength: jsonText.length });
      
      const data = JSON.parse(jsonText);
      
      // If it's an array of objects, convert to CSV format
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
        const headers = Object.keys(data[0]);
        const rows = data.map(item => headers.map(h => String(item[h] ?? '')));
        const csvRows = [headers, ...rows];
        return csvRows.map(row => row.join(',')).join('\n');
      }
      
      // If it's a single object, convert to key-value pairs
      if (typeof data === 'object' && !Array.isArray(data)) {
        const entries = Object.entries(data);
        const csvRows = [['Key', 'Value'], ...entries.map(([k, v]) => [k, String(v)])];
        return csvRows.map(row => row.join(',')).join('\n');
      }
      
      // For other types, just show as text
      return jsonText;
    } catch (error) {
      console.error('[FilePreview] JSON parsing error:', error);
      // If parsing fails, return as-is for text preview
      return jsonText;
    }
  };

  // Reusable table renderer used by both inline and fullscreen views
  const renderTable = (headers: string[], bodyRows: string[][]) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
      <thead>
        <tr style={{ background: 'var(--form-accent)', position: 'sticky', top: 0 }}>
          {headers.map((header, idx) => (
            <th key={idx} style={{
              padding: '8px 12px',
              textAlign: 'left',
              borderBottom: `2px solid ${isDark ? '#444' : '#d0d0d0'}`,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: isDark ? '#e0e0e0' : '#1a1a1a'
            }}>
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {bodyRows.map((row, rowIdx) => (
          <tr key={rowIdx} style={{
            background: rowIdx % 2 === 0 ? (isDark ? '#1a1a1a' : '#ffffff') : (isDark ? '#222' : '#f9f9f9')
          }}>
            {row.map((cell, cellIdx) => (
              <td key={cellIdx} style={{
                padding: '8px 12px',
                borderBottom: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
                whiteSpace: 'nowrap',
                color: isDark ? '#d4d4d4' : '#333'
              }}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderImagePreview = () => {
    if (!imageUrl) {
      console.error('[FilePreview] No image URL available:', { fileName, detectedType, hasFileData: !!fileData });
      return renderUnsupportedPreview();
    }

    logger.log('[FilePreview] Rendering image with URL:', { fileName, imageUrl: imageUrl.substring(0, 50) });

    return (
      <div 
        onClick={() => dispatch(openImage({ src: imageUrl, type: 'image', name: fileName }))}
        style={{
          width: '100%',
          maxHeight: '300px',
          overflow: 'hidden',
          borderRadius: '8px',
          marginBottom: '12px',
          border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
          cursor: 'pointer',
          position: 'relative'
        }}
        title="Click to view full size"
      >
        <img 
          src={imageUrl}
          alt={fileName}
          onLoad={() => {
            logger.log('[FilePreview] Image loaded successfully:', fileName);
            onLoad?.(); // Notify parent that image is loaded
          }}
          onError={(e) => {
            console.error('[FilePreview] Image load error:', fileName, e);
            setPreviewError(true);
            onError?.();
          }}
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            willChange: 'transform', // Hint browser to optimize rendering
            transform: 'translateZ(0)' // Force GPU acceleration
          }}
        />
        <div style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '6px',
          borderRadius: '4px',
          fontSize: '0.85em',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <EnterFullScreenIcon width={16} height={16} />
        </div>
      </div>
    );
  };

  const renderTablePreview = (csvText: string) => {
    try {
      logger.log('[FilePreview] Parsing CSV:', {
        fileName,
        textLength: csvText.length,
        firstChars: csvText.substring(0, 100)
      });
      
      const rows = parseCSV(csvText);
      
      logger.log('[FilePreview] CSV parsed:', {
        fileName,
        rowCount: rows.length,
        columnCount: rows[0]?.length || 0
      });
      
      if (rows.length === 0) {
        console.warn('[FilePreview] CSV has no rows');
        return renderUnsupportedPreview();
      }
      
      const maxRowsPreview = fullscreenMode ? rows.length : 10;
      const displayRows = rows.slice(0, maxRowsPreview);
      const hasMore = !fullscreenMode && rows.length > maxRowsPreview;

      return (
        <div style={{
          marginBottom: '12px',
          border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
          borderRadius: '0px',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <div style={{
            overflowX: 'auto',
            maxHeight: fullscreenMode ? undefined : '400px',
            overflowY: 'auto'
          }}>
            {renderTable(rows[0] || [], displayRows.slice(1))}
          </div>
          <div style={{
            padding: '6px 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--form-accent)',
            borderTop: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
            fontSize: '0.85em',
            color: isDark ? '#aaa' : '#666',
            position: 'relative'
          }}>
            <span>{rows.length - 1} row{rows.length - 1 !== 1 ? 's' : ''}</span>
            {!fullscreenMode && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onRequestFullscreenTable) {
                    onOpenFullscreen?.();
                    onRequestFullscreenTable({ rows, title: fileName });
                  } else {
                    setFullscreenTable({ rows, title: fileName });
                  }
                }}
                style={{
                  position: 'absolute',
                  right: '8px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '4px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.85)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)'}
                title="Open in fullscreen"
                aria-label="Open table in fullscreen"
              >
                <EnterFullScreenIcon width={14} height={14} />
              </button>
            )}
          </div>
        </div>
      );
    } catch (error) {
      console.error('[FilePreview] CSV parsing error:', error);
      return renderUnsupportedPreview();
    }
  };

  const renderTextPreview = (text: string) => {
    const maxLines = fullscreenMode ? Infinity : 20;
    const lines = text.split(/\r?\n/);
    const displayLines = fullscreenMode ? lines : lines.slice(0, maxLines);
    const hasMore = !fullscreenMode && lines.length > maxLines;

    return (
      <div style={{
        marginBottom: '12px',
        border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
        borderRadius: '8px',
        overflow: 'hidden',
        position: 'relative'
      }}>
        <pre style={{
          padding: '12px',
          margin: 0,
          fontSize: '0.85em',
          background: isDark ? '#1a1a1a' : '#f9f9f9',
          color: isDark ? '#e0e0e0' : '#333',
          maxHeight: fullscreenMode ? undefined : '300px',
          overflow: 'auto',
          fontFamily: 'Consolas, Monaco, "Courier New", monospace',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word'
        }}>
          {displayLines.join('\n')}
        </pre>
        {hasMore && (
          <div style={{
            padding: '6px 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--form-accent)',
            borderTop: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
            fontSize: '0.85em',
            color: isDark ? '#aaa' : '#666',
            position: 'relative'
          }}>
            <span>Showing {20} of {lines.length} lines</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (onRequestFullscreenText) {
                  onOpenFullscreen?.();
                  onRequestFullscreenText({ text, title: fileName });
                } else {
                  setFullscreenText({ text, title: fileName });
                }
              }}
              style={{
                position: 'absolute',
                right: '8px',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '4px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.85)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)'}
              title="Open in fullscreen"
              aria-label="Open text in fullscreen"
            >
              <EnterFullScreenIcon width={14} height={14} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderPDFPreview = () => {
    if (!pdfUrl) {
      console.error('[FilePreview] No PDF URL available:', { fileName, detectedType, hasFileData: !!fileData });
      return renderUnsupportedPreview();
    }

    logger.log('[FilePreview] Rendering PDF with URL:', { fileName, pdfUrl: pdfUrl.substring(0, 50) });

    // Hide built-in PDF viewer chrome for a cleaner look.
    const pdfUrlWithZoom = buildPdfViewerSrc(pdfUrl, 50);

    return (
      <div 
        style={{
          marginBottom: '12px',
          border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
          borderRadius: '0px',
          overflow: 'hidden',
          position: 'relative',
          background: isDark ? '#171717' : '#ffffff',
          boxShadow: isDark
            ? '0 8px 24px rgba(0, 0, 0, 0.25)'
            : '0 8px 24px rgba(15, 23, 42, 0.08)'
        }}
      >
        <iframe
          src={pdfUrlWithZoom}
          onLoad={() => {
            logger.log('[FilePreview] PDF loaded successfully:', fileName);
            onLoad?.(); // Notify parent that PDF is loaded
          }}
          style={{
            width: '100%',
            height: '400px',
            border: 'none',
            display: 'block',
            background: isDark ? '#171717' : '#ffffff',
            willChange: 'contents',
            transform: 'translateZ(0)'
          }}
          title={`PDF preview of ${fileName}`}
        />
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenFullscreen?.();
            dispatch(openImage({ src: pdfUrl, type: 'pdf', name: fileName }));
          }}
          style={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '6px',
            borderRadius: '4px',
            fontSize: '0.85em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.85)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)'}
          title="Open in fullscreen"
          aria-label="Open PDF in fullscreen"
        >
          <EnterFullScreenIcon width={16} height={16} />
        </button>
      </div>
    );
  };

  const renderUnsupportedPreview = () => {
    return (
      <div style={{
        marginBottom: '12px',
        padding: '20px',
        border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
        borderRadius: '8px',
        textAlign: 'center',
        background: isDark ? '#1a1a1a' : '#f9f9f9',
        color: isDark ? '#aaa' : '#666'
      }}>
        <ExclamationTriangleIcon 
          width={24} 
          height={24} 
          style={{ 
            marginBottom: '8px',
            color: isDark ? '#888' : '#999'
          }} 
        />
        <div style={{ fontSize: '0.9em' }}>
          Preview not available for this file type
        </div>
        <div style={{ fontSize: '0.8em', marginTop: '4px' }}>
          Use the download button below to view the file
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    if (previewError) {
      return renderUnsupportedPreview();
    }

    try {
      // Log for debugging
      logger.log('[FilePreview] Rendering preview:', {
        fileName,
        detectedType,
        fileDataType: typeof fileData,
        fileDataLength: typeof fileData === 'string' ? fileData.length : fileData.byteLength
      });

      // For text-based files, decode ArrayBuffer if needed
      let textData = '';
      if (detectedType === 'csv' || detectedType === 'tsv' || detectedType === 'text' || detectedType === 'json') {
        if (typeof fileData === 'string') {
          textData = fileData;
        } else {
          try {
            textData = new TextDecoder('utf-8').decode(fileData);
          } catch (decodeError) {
            console.error('[FilePreview] Text decode error:', decodeError);
            return renderUnsupportedPreview();
          }
        }
      }

      switch (detectedType) {
        case 'image':
          return renderImagePreview();
        case 'csv':
          return renderTablePreview(textData);
        case 'tsv':
          // Parse TSV with tab delimiter and render as table
          const tsvRows = parseCSV(textData, '\t');
          const tsvCsv = tsvRows.map(row => row.join(',')).join('\n');
          return renderTablePreview(tsvCsv);
        case 'excel':
          // Use parsed Excel data from state
          if (excelData.status === 'parsing') {
            return (
              <div style={{
                marginBottom: '12px',
                padding: '20px',
                border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
                borderRadius: '8px',
                textAlign: 'center',
                background: isDark ? '#1a1a1a' : '#f9f9f9',
                color: isDark ? '#aaa' : '#666'
              }}>
                <p>Parsing Excel file...</p>
              </div>
            );
          }
          
          if (excelData.status === 'error') {
            return (
              <div style={{
                marginBottom: '12px',
                padding: '20px',
                border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
                borderRadius: '8px',
                textAlign: 'center',
                background: isDark ? '#1a1a1a' : '#f9f9f9',
                color: isDark ? '#aaa' : '#666'
              }}>
                <ExclamationTriangleIcon 
                  width={24} 
                  height={24} 
                  style={{ 
                    display: 'inline-block', 
                    marginBottom: '8px',
                    color: isDark ? '#ff6b6b' : '#d32f2f'
                  }} 
                />
                <p>Failed to parse Excel file</p>
                <p style={{ fontSize: '12px', marginTop: '8px' }}>{excelData.error}</p>
              </div>
            );
          }
          
          if (excelData.status === 'parsed' && excelData.csvData) {
            return renderTablePreview(excelData.csvData);
          }
          
          // Still parsing
          return null;
        case 'text':
          return renderTextPreview(textData);
        case 'json':
          // Try to parse JSON and render as table
          const csvFromJson = parseJSON(textData);
          const jsonRows = parseCSV(csvFromJson);
          if (jsonRows.length > 1) {
            return renderTablePreview(csvFromJson);
          }
          // Fallback to text if parsing failed or resulted in simple text
          return renderTextPreview(textData);
        case 'archive':
          // No preview for archive files (ZIP)
          return null;
        case 'pdf':
          return renderPDFPreview();
        case 'powerpoint':
          // Show loading state while converting
          if (pptConversionState.status === 'converting') {
            return (
              <div style={{
                marginBottom: '12px',
                padding: '20px',
                border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
                borderRadius: '8px',
                textAlign: 'center',
                background: isDark ? '#1a1a1a' : '#f9f9f9',
                color: isDark ? '#aaa' : '#666'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  margin: '0 auto 12px',
                  border: `3px solid ${isDark ? '#444' : '#d0d0d0'}`,
                  borderTop: '3px solid var(--color-accent)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <div style={{ fontSize: '0.9em' }}>
                  Converting PowerPoint to PDF...
                </div>
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            );
          }
          
          // Show error state if conversion failed
          if (pptConversionState.status === 'error') {
            return (
              <div style={{
                marginBottom: '12px',
                padding: '20px',
                border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
                borderRadius: '8px',
                textAlign: 'center',
                background: isDark ? '#1a1a1a' : '#f9f9f9',
                color: isDark ? '#aaa' : '#666'
              }}>
                <ExclamationTriangleIcon 
                  width={24} 
                  height={24} 
                  style={{ 
                    marginBottom: '8px',
                    color: isDark ? '#888' : '#999'
                  }} 
                />
                <div style={{ fontSize: '0.9em' }}>
                  Failed to convert PowerPoint to PDF
                </div>
                <div style={{ fontSize: '0.8em', marginTop: '4px' }}>
                  {pptConversionState.error || 'Use the download button below to open the file'}
                </div>
              </div>
            );
          }
          
          // Show converted PDF
          if (pptConversionState.status === 'converted' && pptConversionState.pdfData) {
            // Cache a stable PDF data URI so preview/fullscreen don't depend on blob URLs
            if (!pptPdfUrlRef.current) {
              pptPdfUrlRef.current = `data:application/pdf;base64,${pptConversionState.pdfData}`;
            }
            const pdfUrl = pptPdfUrlRef.current;
            return renderConvertedPdfPreview({
              pdfUrl,
              bannerTitle: '📄 Text-based PDF Preview',
              bannerMessage: 'This shows slide content as text. Visual elements (images, charts, tables, formatting) may not be displayed properly. Download the original PowerPoint for full formatting.',
              showInfoBanner: showPptInfoBanner,
              onDismissInfoBanner: () => setShowPptInfoBanner(false),
              loadLogMessage: '[FilePreview] Converted PowerPoint PDF loaded',
              errorLogMessage: '[FilePreview] Converted PowerPoint PDF load error',
            });
          }
          
          // Fallback (shouldn't reach here, but just in case)
          return renderUnsupportedPreview();
        case 'word':
          // Show loading state while converting
          if (docConversionState.status === 'converting') {
            return (
              <div style={{
                marginBottom: '12px',
                padding: '20px',
                border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
                borderRadius: '8px',
                textAlign: 'center',
                background: isDark ? '#1a1a1a' : '#f9f9f9',
                color: isDark ? '#aaa' : '#666'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  margin: '0 auto 12px',
                  border: `3px solid ${isDark ? '#444' : '#d0d0d0'}`,
                  borderTop: '3px solid var(--color-accent)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <div style={{ fontSize: '0.9em' }}>
                  Converting Word document to PDF...
                </div>
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            );
          }
          
          // Show error state if conversion failed
          if (docConversionState.status === 'error') {
            return (
              <div style={{
                marginBottom: '12px',
                padding: '20px',
                border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
                borderRadius: '8px',
                textAlign: 'center',
                background: isDark ? '#1a1a1a' : '#f9f9f9',
                color: isDark ? '#aaa' : '#666'
              }}>
                <ExclamationTriangleIcon 
                  width={24} 
                  height={24} 
                  style={{ 
                    marginBottom: '8px',
                    color: isDark ? '#888' : '#999'
                  }} 
                />
                <div style={{ fontSize: '0.9em' }}>
                  Failed to convert Word document to PDF
                </div>
                <div style={{ fontSize: '0.8em', marginTop: '4px' }}>
                  {docConversionState.error || 'Use the download button below to open the file'}
                </div>
              </div>
            );
          }
          
          // Show converted PDF
          if (docConversionState.status === 'converted' && docConversionState.pdfData) {
            // Cache a stable PDF data URI so preview/fullscreen don't depend on blob URLs
            if (!docPdfUrlRef.current) {
              docPdfUrlRef.current = `data:application/pdf;base64,${docConversionState.pdfData}`;
            }
            const pdfUrl = docPdfUrlRef.current;
            return renderConvertedPdfPreview({
              pdfUrl,
              bannerTitle: '📄 PDF Preview',
              bannerMessage: 'This document has been converted to PDF for preview. Some formatting may differ from the original. Download the original Word document for full fidelity.',
              showInfoBanner: showDocInfoBanner,
              onDismissInfoBanner: () => setShowDocInfoBanner(false),
              loadLogMessage: '[FilePreview] Converted Word PDF loaded',
              errorLogMessage: '[FilePreview] Converted Word PDF load error',
            });
          }
          
          // Fallback
          return renderUnsupportedPreview();
        default:
          console.warn('[FilePreview] Unknown file type:', detectedType);
          return renderUnsupportedPreview();
      }
    } catch (error) {
      console.error('[FilePreview] Render error:', error);
      return renderUnsupportedPreview();
    }
  };

  return (
    <div style={{ marginTop: '12px', marginBottom: '8px' }}>
      {renderPreview()}

      {/* Fullscreen table modal */}
      <FullscreenDataModal
        isOpen={!!fullscreenTable}
        onClose={() => setFullscreenTable(null)}
        title={fullscreenTable?.title || fileName}
      >
        {fullscreenTable && (
          <div style={{ overflowX: 'auto' }}>
            {renderTable(fullscreenTable.rows[0] || [], fullscreenTable.rows.slice(1))}
            <div style={{
              padding: '8px',
              textAlign: 'center',
              background: 'var(--form-accent)',
              fontSize: '0.85em',
              color: isDark ? '#aaa' : '#666'
            }}>
              {fullscreenTable.rows.length - 1} row{fullscreenTable.rows.length - 1 !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </FullscreenDataModal>

      {/* Fullscreen text modal */}
      <FullscreenDataModal
        isOpen={!!fullscreenText}
        onClose={() => setFullscreenText(null)}
        title={fullscreenText?.title || fileName}
      >
        {fullscreenText && (
          <pre style={{
            padding: '16px',
            margin: 0,
            fontSize: '0.85em',
            background: isDark ? '#1a1a1a' : '#f9f9f9',
            color: isDark ? '#e0e0e0' : '#333',
            overflow: 'auto',
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            borderRadius: '8px'
          }}>
            {fullscreenText.text}
          </pre>
        )}
      </FullscreenDataModal>
    </div>
  );
};

// Memoize to prevent unnecessary re-renders when parent re-renders
export const FilePreview = React.memo(FilePreviewComponent, (prevProps, nextProps) => {
  // Only re-render if fileName changes (fileData content should be the same for same file)
  return prevProps.fileName === nextProps.fileName && 
         prevProps.fileType === nextProps.fileType &&
         prevProps.fileData === nextProps.fileData &&
         prevProps.fullscreenMode === nextProps.fullscreenMode;
});